/**
 * AuthGuard component wraps protected parts of the app
 * Ensures user is logged in before displaying content
 */

import React, { useState } from "react";
import { useAuth } from "../lib/auth-context";
import { reportError } from "../lib/newrelic";
import Loading from "./Loading";
import type { AuthGuardProps } from "../types/index";

function LockIcon(): JSX.Element {
  return (
    <svg
      width="28"
      height="28"
      viewBox="0 0 24 24"
      fill="none"
      stroke="var(--color-brand)"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function MicrosoftLogo(): JSX.Element {
  return (
    <svg width="20" height="20" viewBox="0 0 21 21">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

function InfoIcon(): JSX.Element {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="var(--color-label)"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  );
}

function LoginScreen({
  onLogin,
}: {
  onLogin: () => void;
}): JSX.Element {
  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "var(--color-surface-page)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "2rem",
      }}
    >
      <div
        style={{
          backgroundColor: "var(--color-surface)",
          borderRadius: "16px",
          boxShadow: "0 2px 16px var(--color-shadow)",
          padding: "3rem 2.5rem",
          maxWidth: "420px",
          width: "100%",
          textAlign: "center",
        }}
      >
        {/* Lock icon */}
        <div
          style={{
            width: "56px",
            height: "56px",
            backgroundColor: "var(--color-brand-light)",
            borderRadius: "14px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 1.5rem",
          }}
        >
          <LockIcon />
        </div>

        {/* Heading */}
        <h1
          style={{
            fontSize: "1.5rem",
            fontWeight: 700,
            color: "var(--color-heading)",
            margin: "0 0 0.5rem",
          }}
        >
          Sign in to AI Marketplace
        </h1>

        {/* Subtitle */}
        <p
          style={{
            color: "var(--color-text-secondary)",
            fontSize: "0.95rem",
            lineHeight: 1.5,
            margin: "0 0 2rem",
          }}
        >
          Use your corporate account to access plug-ins and
          skills, manage your library, and contribute to the
          marketplace.
        </p>

        {/* Sign-in button */}
        <button
          onClick={onLogin}
          style={{
            width: "100%",
            padding: "0.85rem 1.5rem",
            fontSize: "1rem",
            fontWeight: 600,
            backgroundColor: "var(--color-brand-emphasis)",
            color: "var(--color-on-brand)",
            border: "none",
            borderRadius: "10px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.6rem",
          }}
        >
          <MicrosoftLogo />
          Sign in with Microsoft Entra
        </button>

        {/* INFO divider */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            margin: "1.75rem 0 1.25rem",
            gap: "0.75rem",
          }}
        >
          <div style={{ flex: 1, height: "1px", backgroundColor: "var(--color-border-header)" }} />
          <span
            style={{
              color: "var(--color-text-faint)",
              fontSize: "0.75rem",
              fontWeight: 600,
              letterSpacing: "0.05em",
            }}
          >
            INFO
          </span>
          <div style={{ flex: 1, height: "1px", backgroundColor: "var(--color-border-header)" }} />
        </div>

        {/* Info callout */}
        <div
          style={{
            backgroundColor: "var(--color-surface-page)",
            border: "1px solid var(--color-border)",
            borderRadius: "10px",
            padding: "1rem 1.25rem",
            display: "flex",
            alignItems: "flex-start",
            gap: "0.75rem",
            textAlign: "left",
          }}
        >
          <div style={{ flexShrink: 0, marginTop: "1px" }}>
            <InfoIcon />
          </div>
          <p
            style={{
              margin: 0,
              color: "var(--color-text-pill)",
              fontSize: "0.85rem",
              lineHeight: 1.5,
            }}
          >
            Sign in requires your Cox Automotive corporate
            credentials via Microsoft Entra ID. Contact IT
            Support if you're unable to access your account.
          </p>
        </div>
      </div>
    </div>
  );
}

export default function AuthGuard({ children }: AuthGuardProps): JSX.Element {
  // Initialization, session state, and init errors are owned by AuthProvider.
  const { initialized, isAuthenticated, skipAuth, error: initError, login } = useAuth();
  // Login redirects navigate away, so this only catches a synchronous launch
  // failure (e.g. interaction_in_progress). Init errors come from context.
  const [loginError, setLoginError] = useState<string | null>(null);
  const authError = initError ?? loginError;
  const authenticated = skipAuth || isAuthenticated;

  if (!initialized) {
    return <Loading message="Initializing authentication..." />;
  }

  if (authError) {
    return (
      <div
        style={{
          minHeight: "100vh",
          backgroundColor: "var(--color-surface-page)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "2rem",
        }}
      >
        <div
          style={{
            backgroundColor: "var(--color-surface)",
            borderRadius: "16px",
            boxShadow: "0 2px 16px var(--color-shadow)",
            padding: "3rem 2.5rem",
            maxWidth: "420px",
            width: "100%",
            textAlign: "center",
          }}
        >
          <h1 style={{ fontSize: "1.5rem", fontWeight: 700, color: "var(--color-heading)", margin: "0 0 0.5rem" }}>
            Authentication Error
          </h1>
          <p style={{ color: "var(--color-error)", fontSize: "0.95rem", margin: "0 0 1rem" }}>
            {authError}
          </p>
          <p style={{ color: "var(--color-text-secondary)", fontSize: "0.9rem", margin: "0 0 1.5rem" }}>
            Please check your Entra configuration and try again.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: "0.75rem 1.5rem",
              fontSize: "1rem",
              fontWeight: 600,
              backgroundColor: "var(--color-brand-emphasis)",
              color: "var(--color-on-brand)",
              border: "none",
              borderRadius: "10px",
              cursor: "pointer",
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!authenticated) {
    return (
      <LoginScreen
        onLogin={() => {
          login().catch((error: unknown) => {
            reportError(error, "auth-login");
            setLoginError(error instanceof Error ? error.message : String(error));
          });
        }}
      />
    );
  }

  return <>{children}</>;
}
